import React from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import PersonalisedInterview from 'components/PersonalisedInterview'
import CodingInterview from 'components/CodingInterview';
import CaseMaster from 'components/CaseMaster';
import AppShell from 'components/AppShell';
import Toggle from 'components/Toggle';
import CodingInterviewLaunchDialog from 'dialogs/CodingInterviewLaunchDialog';
import { useNavigate, useSearchParams } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  tooltipBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '400px',
    padding: '10px',
  }
}));

const chatIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M2.99983 9C2.99983 4.58172 6.58125 1 10.9992 1C15.4171 1 18.9985 4.58172 18.9985 9C18.9985 10.15 18.7558 11.2434 18.319 12.2316L19 16.9992L14.9146 15.9778C13.7572 16.6287 12.4215 17 10.9992 17M1.00095 14C1.00095 14.7188 1.15258 15.4021 1.4256 16.0198L1 18.9995L3.55315 18.3611C4.27643 18.768 5.11115 19 6.00005 19C8.76098 19 10.9992 16.7614 10.9992 14C10.9992 11.2386 8.76098 9 6.00005 9C3.23912 9 1.00095 11.2386 1.00095 14Z" stroke="#3C434D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const codeIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
    <path d="M8.59844 13.9984L5.59844 10.9984L8.59844 7.99844M13.3984 7.99844L16.3984 10.9984L13.3984 13.9984M3.79844 20.5984C2.47295 20.5984 1.39844 19.5239 1.39844 18.1984V3.79844C1.39844 2.47295 2.47295 1.39844 3.79844 1.39844H18.1984C19.5239 1.39844 20.5984 2.47295 20.5984 3.79844V18.1984C20.5984 19.5239 19.5239 20.5984 18.1984 20.5984H3.79844Z" stroke="#3C434D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const caseMasterIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M11.7261 20.2286V5.4172M11.7261 20.2286L10.2686 18.7712C9.44561 17.9481 8.32986 17.4858 7.16594 17.4858H3.49658C2.89064 17.4858 2.40039 16.9946 2.40039 16.3886V4.86863C2.40039 4.26269 2.8916 3.77148 3.49753 3.77148H7.71401C8.87793 3.77148 9.99418 4.23385 10.8172 5.05687L11.7261 5.96577L12.635 5.05687C13.458 4.23385 14.5743 3.77148 15.7382 3.77148H20.5032C21.1092 3.77148 21.6004 4.26269 21.6004 4.86863V16.3886C21.6004 16.9946 21.1092 17.4858 20.5032 17.4858H16.2868C15.1228 17.4858 14.0066 17.9481 13.1836 18.7712L11.7261 20.2286Z" stroke="#3C434C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
);

const ToggleTooltip = ({ title, description }) => {
  const classes = useStyles();

  return (
    <Box className={classes.tooltipBox}>
      <Typography variant='h5-semiBold' color='neutral.clr-900'>
        {title}
      </Typography>
      <Typography variant='body01-bold' color='neutral.clr-600'>
        {description}
      </Typography>
    </Box>
  );
};

const InterviewWizard = () => {
  const [activeTab, setActiveTab] = React.useState(0);
  const [openNewLaunchDialog, setOpenNewLaunchDialog] = React.useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  React.useEffect(() => {
    const type = searchParams.get("type");
  
    if(type==='coding') setActiveTab(1);
    else if(type==='case-master') setActiveTab(2);
    else setActiveTab(0);

  }, [searchParams]);

  React.useEffect(() => {
    if (!localStorage.getItem("coding-round-dialog-opened")) {
      setOpenNewLaunchDialog(true)
      localStorage.setItem("coding-round-dialog-opened", true.toString());
    }
  }, []);
  
  return (
    <AppShell 
      headerLabel='Interview Wizard'
      headerChildren={
        <Toggle
            width={684}
            height={50}
            options={[
              {id: 0, code: 'verbal', label: 'Verbal Interview', icon: chatIcon}, 
              {id: 1, code: 'coding', label: 'Coding Interview', icon: codeIcon},
              {id: 2, code: 'case-master', label: 'Case Master', icon: caseMasterIcon}
            ]}
            value={activeTab}
            onChange={(e)=>{
              setActiveTab(e.id);
              navigate(`?type=${e.code}`);
            }}
            tooltips={[
              <ToggleTooltip
                title="Personalized Verbal Interviews"
                description="Prepare for different interview rounds such HR, Managerial, Technical and more based on your resume and company's job description."
              />,
              <ToggleTooltip
                title="Coding Interviews"
                description="Prepare with previous year coding questions, DSA topics & more in a virtual interview environment."
              />,
              <ToggleTooltip
                title="Case Master Interviews"
                description="Master case study interviews with business scenarios, problem-solving frameworks, and structured thinking approaches used in consulting and strategic roles."
              />
            ]}
          />
      }
    >
      {
        activeTab===0 && <PersonalisedInterview/>
      }
      {
        activeTab===1 && <CodingInterview/>
      }
      {
        activeTab===2 && <CaseMaster/>
      }

      <CodingInterviewLaunchDialog 
        openDialog={openNewLaunchDialog} 
        onClose={setOpenNewLaunchDialog}
        onSuccess={setActiveTab}
      />
    </AppShell>
  )
}

export default InterviewWizard